import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useRef, useState } from 'react';
import { CreativeIdLine } from '../../creatives/CreativeIdLine';
import { MediaCard } from '../../media/shared/MediaCard';
import { DeltaChip } from '../DeltaChip';
import type { CreativeCard as CreativeCardData } from './data/api';

const MEDIA_HEIGHT = 140;
const MEDIA_FADE = 'linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,0) 60%)';
const STAT_LABEL_SX = {
  fontSize: 10,
  fontWeight: 500,
  color: 'text.secondary',
  textTransform: 'uppercase',
} as const;
const STATUS: Record<CardStatus, { label: string; color: string; bg: string; value: string }> = {
  top: { label: 'Top performer', color: 'success.main', bg: 'success.bg', value: 'success.main' },
  under: { label: 'Underperformer', color: 'error.main', bg: 'error.bg', value: 'error.main' },
  fatigued: {
    label: 'Currently fatigued',
    color: 'warning.dark',
    bg: 'warning.bg',
    value: 'text.primary',
  },
  hook: { label: 'Top hook', color: 'info.main', bg: 'info.bg', value: 'text.primary' },
};

export type CardStatus = 'top' | 'under' | 'fatigued' | 'hook';

export interface CardStat {
  label: string;
  value: string;
}

interface CreativeCardProps {
  creative: CreativeCardData;
  status: CardStatus;
  primary: CardStat;
  stats: CardStat[];
  /** Replaces the creative type under the id, e.g. the fatigue alert date. */
  meta?: string;
  /** Hide the period delta next to the primary value. */
  hideDelta?: boolean;
  onOpen?: (creativeId: string) => void;
}

function formatDuration(seconds: number | null): string | null {
  if (seconds === null || seconds <= 0) return null;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export function CreativeCard({
  creative,
  status,
  primary,
  stats,
  meta,
  hideDelta = false,
  onOpen,
}: CreativeCardProps): ReactNode {
  const tone = STATUS[status];
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const creativeType = creative.creativeType?.toLowerCase();
  const previewUrl = creativeType === 'image' ? null : creative.previewUrl || null;
  const isVideo = creativeType === 'video' || previewUrl !== null;
  const duration = formatDuration(creative.duration);
  const delta = creative.kpiImprovement;
  const metaLine = meta ?? creative.creativeType;

  function handleMouseEnter(): void {
    if (previewUrl === null) return;
    setIsPlaying(true);
    const video = videoRef.current;
    // A superseded play() rejects after a newer one started; only a still-paused video failed.
    video?.play().catch(() => {
      if (video.paused) setIsPlaying(false);
    });
  }

  function handleMouseLeave(): void {
    setIsPlaying(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  return (
    <MediaCard
      sx={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100%',
      }}
    >
      <Box
        data-test="report-creative-card"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ position: 'relative', height: MEDIA_HEIGHT, bgcolor: 'background.platform' }}
      >
        <ButtonBase
          aria-label={`Open ${creative.creativeId}`}
          onClick={() => onOpen?.(creative.creativeId)}
          sx={{ position: 'absolute', inset: 0, display: 'block' }}
        >
          {creative.thumbnailUrl === null ? null : (
            <Box
              component="img"
              src={creative.thumbnailUrl}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          {previewUrl === null ? null : (
            <Box
              component="video"
              ref={videoRef}
              src={previewUrl}
              muted
              loop
              playsInline
              preload="none"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: isPlaying ? 1 : 0,
                transition: 'opacity 150ms ease',
                pointerEvents: 'none',
              }}
            />
          )}
          <Box sx={{ position: 'absolute', inset: 0, background: MEDIA_FADE }} />
          {isVideo && !isPlaying ? (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
              <PlayArrowRounded titleAccess="Video" sx={{ fontSize: 22, color: 'text.primary' }} />
            </Box>
          ) : null}
        </ButtonBase>
        <Box
          component="span"
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            px: '6px',
            py: '4px',
            borderRadius: '6px',
            bgcolor: tone.bg,
            opacity: 0.7,
            color: tone.color,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            pointerEvents: 'none',
          }}
        >
          {tone.label}
        </Box>
        {duration === null ? null : (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              px: '6px',
              py: '2px',
              borderRadius: '6px',
              bgcolor: 'rgba(255,255,255,.85)',
              color: 'text.primary',
              fontSize: 11,
              fontWeight: 600,
              pointerEvents: 'none',
            }}
          >
            {duration}
          </Box>
        )}
      </Box>
      <Stack sx={{ p: '16px', gap: '12px', flex: 1 }}>
        <Stack sx={{ gap: '2px', minWidth: 0 }}>
          <CreativeIdLine creativeId={creative.creativeId} />
          {metaLine === null ? null : (
            <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
              {metaLine}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" sx={{ gap: '8px', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 26, fontWeight: 600, color: tone.value, lineHeight: 1.1 }}>
            {primary.value}
          </Typography>
          <Typography sx={STAT_LABEL_SX}>{primary.label}</Typography>
          {!hideDelta && delta !== null && <DeltaChip value={delta} />}
        </Stack>
        <Box sx={{ height: '1px', bgcolor: 'divider' }} />
        <Box sx={{ display: 'grid', gridAutoFlow: 'column', gap: '16px' }}>
          {stats.map((stat) => (
            <Stack key={stat.label} sx={{ gap: '2px', minWidth: 0 }}>
              <Typography sx={STAT_LABEL_SX}>{stat.label}</Typography>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                {stat.value}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Stack>
    </MediaCard>
  );
}
